/**
 * Shared retailer branding model, provider, and CSS color application.
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";

export interface BrandTheme {
  id: string;
  name: string;
  logo_url: string | null;
  logo_dark_mode: string | null;
  logo_size: number | null;
  primary_color: string;
  secondary_color: string | null;
  accent_color: string | null;
  is_live: boolean;
}

interface BrandThemeContextValue {
  theme: BrandTheme | null;
  isLoading: boolean;
  error: string | null;
}

const BrandThemeContext = createContext<BrandThemeContextValue>({
  theme: null,
  isLoading: true,
  error: null,
});

// eslint-disable-next-line react-refresh/only-export-components
export function useBrandTheme() {
  return useContext(BrandThemeContext);
}

/**
 * Get Tailwind height class from logo_size value (1-10)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getLogoHeightClass(size: number | null | undefined): string {
  if (!size) return "h-18";
  const sizeMap: Record<number, string> = {
    1: "h-10",
    2: "h-12",
    3: "h-14",
    4: "h-16",
    5: "h-18",
    6: "h-20",
    7: "h-22",
    8: "h-24",
    9: "h-26",
    10: "h-28",
  };
  return sizeMap[size] || "h-18";
}

/**
 * Convert hex color to HSL for Tailwind CSS variables
 */
function hexToHsl(hex: string): string {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Calculate logo background color based on brand primary color.
 * Uses a darker background for dark brand colors (common with white logos)
 * and a lighter background for light brand colors (common with dark logos).
 */
function getLogoBackgroundColor(hex: string): string {
  hex = hex.replace(/^#/, "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Dark brand colors typically pair with white/light logos → use dark bg
  // Light brand colors typically pair with dark logos → use light bg
  return luminance > 0.5 ? "#f3f4f6" : "#1f2937"; // gray-100 or gray-800
}

/** The parts of GET /v1/sessions/:id the brand theme is built from. */
export interface SessionBrandSource {
  session: { retailer_id: string };
  retailer?: RetailerBrandData | null;
}

export interface RetailerBrandData {
  name?: string | null;
  logo_url?: string | null;
  logo_dark_mode?: string | null;
  logo_size?: number | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
  is_live?: boolean | null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function brandThemeFromRetailerData(
  id: string,
  retailer: RetailerBrandData | null | undefined,
): BrandTheme {
  return {
    id,
    name: retailer?.name || "Store",
    logo_url: retailer?.logo_url || null,
    logo_dark_mode: retailer?.logo_dark_mode || null,
    logo_size: retailer?.logo_size || null,
    primary_color: retailer?.primary_color || "#0ea5e9",
    secondary_color: retailer?.secondary_color || null,
    accent_color: retailer?.accent_color || null,
    is_live: retailer?.is_live ?? false,
  };
}

/**
 * The brand theme for a session (GET /v1/sessions/:id): shared by the checkout
 * (BrandThemeProvider) and the express cart, so both look the same.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function brandThemeFromSession(data: SessionBrandSource): BrandTheme {
  return {
    id: data.session.retailer_id,
    name: data.retailer?.name || "Store",
    logo_url: data.retailer?.logo_url || null,
    logo_dark_mode: data.retailer?.logo_dark_mode || null,
    logo_size: data.retailer?.logo_size || null,
    primary_color: data.retailer?.primary_color || "#0ea5e9",
    secondary_color: data.retailer?.secondary_color || null,
    accent_color: data.retailer?.accent_color || null, //84BF41
    is_live: data.retailer?.is_live ?? false,
  };
}

/**
 * Apply brand colors as CSS custom properties
 */
// eslint-disable-next-line react-refresh/only-export-components
export function applyBrandColors(theme: BrandTheme) {
  const root = document.documentElement;

  // Remove all previously set brand color variables for optional fields (secondary_color, accent_color) to avoid stale values
  const variables = [
    "--color-primary", "--color-accent", "--color-logo-bg", "--color-primary-hover",
    "--color-primary-light", "--color-brand-500", "--color-brand-600", "--color-brand-700",
    "--color-brand-400", "--color-brand-300", "--color-brand-200", "--color-brand-100",
    "--color-brand-50", "--brand-primary", "--color-secondary", "--color-secondary-hover",
    "--color-secondary-light", "--color-secondary-500", "--color-secondary-600",
    "--color-secondary-700", "--color-secondary-400", "--color-secondary-300",
    "--color-secondary-200", "--color-secondary-100", "--color-secondary-50",
    "--brand-secondary", "--color-accent-custom", "--color-accent-hover", "--color-accent-light",
    "--color-accent-500", "--color-accent-600", "--color-accent-700", "--color-accent-400",
    "--color-accent-300", "--color-accent-200", "--color-accent-100", "--color-accent-50",
    "--brand-accent",
  ];
  variables.forEach((variable) => root.style.removeProperty(variable));

  if (theme.primary_color) {
    const primaryHsl = hexToHsl(theme.primary_color);
    const logoBg = getLogoBackgroundColor(theme.primary_color);

    // Set the main primary color (used by index.css)
    root.style.setProperty("--color-primary", theme.primary_color);
    root.style.setProperty("--color-accent", theme.primary_color);

    // Set logo background to contrast with primary brand color
    root.style.setProperty("--color-logo-bg", logoBg);

    // Generate hover and light variants
    root.style.setProperty("--color-primary-hover", darkenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-primary-light", lightenColor(theme.primary_color, 0.4));

    // Set brand color scale
    root.style.setProperty("--color-brand-500", theme.primary_color);
    root.style.setProperty("--color-brand-600", darkenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-brand-700", darkenColor(theme.primary_color, 0.2));
    root.style.setProperty("--color-brand-400", lightenColor(theme.primary_color, 0.1));
    root.style.setProperty("--color-brand-300", lightenColor(theme.primary_color, 0.25));
    root.style.setProperty("--color-brand-200", lightenColor(theme.primary_color, 0.4));
    root.style.setProperty("--color-brand-100", lightenColor(theme.primary_color, 0.6));
    root.style.setProperty("--color-brand-50", lightenColor(theme.primary_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-primary", primaryHsl);
  }

  if (theme.secondary_color) {
    const secondaryHsl = hexToHsl(theme.secondary_color);

    // Set the main secondary color
    root.style.setProperty("--color-secondary", theme.secondary_color);

    // Generate hover and light variants
    root.style.setProperty("--color-secondary-hover", darkenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-light", lightenColor(theme.secondary_color, 0.4));

    // Set secondary color scale
    root.style.setProperty("--color-secondary-500", theme.secondary_color);
    root.style.setProperty("--color-secondary-600", darkenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-700", darkenColor(theme.secondary_color, 0.2));
    root.style.setProperty("--color-secondary-400", lightenColor(theme.secondary_color, 0.1));
    root.style.setProperty("--color-secondary-300", lightenColor(theme.secondary_color, 0.25));
    root.style.setProperty("--color-secondary-200", lightenColor(theme.secondary_color, 0.4));
    root.style.setProperty("--color-secondary-100", lightenColor(theme.secondary_color, 0.6));
    root.style.setProperty("--color-secondary-50", lightenColor(theme.secondary_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-secondary", secondaryHsl);
  }

  if (theme.accent_color) {
    const accentHsl = hexToHsl(theme.accent_color);

    // Set the main accent color
    root.style.setProperty("--color-accent-custom", theme.accent_color);

    // Generate hover and light variants
    root.style.setProperty("--color-accent-hover", darkenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-light", lightenColor(theme.accent_color, 0.4));

    // Set accent color scale
    root.style.setProperty("--color-accent-500", theme.accent_color);
    root.style.setProperty("--color-accent-600", darkenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-700", darkenColor(theme.accent_color, 0.2));
    root.style.setProperty("--color-accent-400", lightenColor(theme.accent_color, 0.1));
    root.style.setProperty("--color-accent-300", lightenColor(theme.accent_color, 0.25));
    root.style.setProperty("--color-accent-200", lightenColor(theme.accent_color, 0.4));
    root.style.setProperty("--color-accent-100", lightenColor(theme.accent_color, 0.6));
    root.style.setProperty("--color-accent-50", lightenColor(theme.accent_color, 0.85));

    // Legacy brand variables (for backwards compatibility)
    root.style.setProperty("--brand-accent", accentHsl);
  }
}

/**
 * Lighten a hex color by a factor (0-1)
 */
function lightenColor(hex: string, factor: number): string {
  hex = hex.replace(/^#/, "");
  const r = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(0, 2), 16) + (255 - parseInt(hex.substring(0, 2), 16)) * factor
    )
  );
  const g = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(2, 4), 16) + (255 - parseInt(hex.substring(2, 4), 16)) * factor
    )
  );
  const b = Math.min(
    255,
    Math.round(
      parseInt(hex.substring(4, 6), 16) + (255 - parseInt(hex.substring(4, 6), 16)) * factor
    )
  );
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Darken a hex color by a factor (0-1)
 */
function darkenColor(hex: string, factor: number): string {
  hex = hex.replace(/^#/, "");
  const r = Math.max(0, Math.round(parseInt(hex.substring(0, 2), 16) * (1 - factor)));
  const g = Math.max(0, Math.round(parseInt(hex.substring(2, 4), 16) * (1 - factor)));
  const b = Math.max(0, Math.round(parseInt(hex.substring(4, 6), 16) * (1 - factor)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

interface BrandThemeProviderProps {
  children: ReactNode;
  theme: BrandTheme | null;
  isLoading?: boolean;
  error?: string | null;
}

export function BrandThemeProvider({ children, theme, isLoading = false, error = null }: BrandThemeProviderProps) {
  useEffect(() => {
    if (theme) applyBrandColors(theme);
    return () => applyBrandColors({
      id: "", name: "Default", logo_url: null, logo_dark_mode: null, logo_size: null,
      primary_color: "", secondary_color: null, accent_color: null, is_live: false,
    });
  }, [theme]);

  return (
    <BrandThemeContext.Provider value={{ theme, isLoading, error }}>
      {children}
    </BrandThemeContext.Provider>
  );
}
